import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CrossPatternComparisonService } from '@/services/CrossPatternComparisonService';
import { CrossPatternDetailModal } from './CrossPatternDetailModal';
import { Download, Search, Filter, TrendingUp, TrendingDown, Equal, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

interface CrossPatternComparisonTabProps {
  refreshTrigger?: number;
}

interface ComparisonSourceInfo {
  type: 'trading_file' | 'honest_simulation';
  id: string;
  name: string;
  analysis_mode?: string;
}

interface PatternComparisonResult {
  pattern: string;
  source1_data?: any;
  source2_data?: any;
  status: 'identical' | 'different_values' | 'only_in_source1' | 'only_in_source2';
  differences?: {
    frequency_diff?: number;
    success_rate_diff?: number;
    total_sequences_diff?: number;
  };
}

interface ComparisonSummary {
  total_patterns_source1: number;
  total_patterns_source2: number;
  identical_patterns: number;
  different_value_patterns: number;
  only_in_source1: number;
  only_in_source2: number;
  comparison_details: PatternComparisonResult[];
}

export const CrossPatternComparisonTab: React.FC<CrossPatternComparisonTabProps> = ({ 
  refreshTrigger 
}) => {
  const [tradingFiles, setTradingFiles] = useState<any[]>([]);
  const [honestSimulations, setHonestSimulations] = useState<any[]>([]);
  const [source1, setSource1] = useState<ComparisonSourceInfo | null>(null);
  const [source2, setSource2] = useState<ComparisonSourceInfo | null>(null);
  const [comparison, setComparison] = useState<ComparisonSummary | null>(null);
  const [filteredPatterns, setFilteredPatterns] = useState<PatternComparisonResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<PatternComparisonResult | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('pattern');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Load available data sources
  useEffect(() => {
    const loadDataSources = async () => {
      try {
        const [filesResult, simulationsResult] = await Promise.all([
          CrossPatternComparisonService.getTradingFiles(),
          CrossPatternComparisonService.getHonestBacktestSimulations()
        ]);

        if (filesResult.success && filesResult.data) {
          setTradingFiles(filesResult.data);
        }

        if (simulationsResult.success && simulationsResult.data) {
          setHonestSimulations(simulationsResult.data);
        }
      } catch (error) {
        console.error('Error loading data sources:', error);
        toast.error('Failed to load data sources');
      }
    };

    loadDataSources();
  }, [refreshTrigger]);

  // Filter and sort patterns
  useEffect(() => {
    if (!comparison) {
      setFilteredPatterns([]);
      return;
    }

    let filtered = [...comparison.comparison_details];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pattern =>
        pattern.pattern?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pattern => pattern.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = '';
      let bValue: any = '';

      switch (sortBy) {
        case 'pattern':
          aValue = a.pattern || '';
          bValue = b.pattern || '';
          break;
        case 'frequency_diff':
          aValue = a.differences?.frequency_diff || 0;
          bValue = b.differences?.frequency_diff || 0;
          break;
        case 'success_rate_diff':
          aValue = a.differences?.success_rate_diff || 0;
          bValue = b.differences?.success_rate_diff || 0;
          break;
        case 'source1_frequency':
          aValue = a.source1_data?.frequency || 0;
          bValue = b.source1_data?.frequency || 0;
          break;
        case 'source2_frequency':
          aValue = a.source2_data?.frequency || 0;
          bValue = b.source2_data?.frequency || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    setFilteredPatterns(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [comparison, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleComparePatterns = async () => {
    if (!source1 || !source2) {
      toast.error('Please select both sources for comparison');
      return;
    }

    setLoading(true);
    try {
      const result = await CrossPatternComparisonService.comparePatterns(source1, source2);
      
      if (result.success && result.data) {
        setComparison(result.data);
        toast.success(`Comparison completed! Found ${result.data.comparison_details.length} patterns to analyze.`);
      } else {
        toast.error(result.error || 'Comparison failed');
      }
    } catch (error) {
      console.error('Error comparing patterns:', error);
      toast.error('Failed to compare patterns');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!comparison) return;

    const csvData = comparison.comparison_details.map(pattern => ({
      Pattern: pattern.pattern,
      Status: pattern.status,
      'Source 1 Frequency': pattern.source1_data?.frequency || 'N/A',
      'Source 1 Success Rate': pattern.source1_data?.success_rate ? `${pattern.source1_data.success_rate}%` : 'N/A',
      'Source 2 Frequency': pattern.source2_data?.frequency || 'N/A',
      'Source 2 Success Rate': pattern.source2_data?.success_rate ? `${pattern.source2_data.success_rate}%` : 'N/A',
      'Frequency Difference': pattern.differences?.frequency_diff || 0,
      'Success Rate Difference': pattern.differences?.success_rate_diff ? `${pattern.differences.success_rate_diff}%` : '0%'
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pattern-comparison-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      identical: 'bg-green-500/10 text-green-600 border-green-500/20',
      different_values: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      only_in_source1: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      only_in_source2: 'bg-purple-500/10 text-purple-600 border-purple-500/20'
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || 'bg-gray-500/10 text-gray-600'}>
        {status.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const getChangeIcon = (change: number | null | undefined) => {
    if (!change || change === 0) return <Equal className="h-4 w-4 text-gray-400" />;
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const formatChange = (change: number | null | undefined) => {
    if (!change) return '0';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}`;
  };

  const getPercentage = (value: number, total: number) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  // Pagination
  const totalPages = Math.ceil(filteredPatterns.length / itemsPerPage);
  const paginatedPatterns = filteredPatterns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      {/* Source Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Cross-Pattern Comparison</CardTitle>
          <CardDescription>
            Compare patterns between trading files and honest backtest simulations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source 1 */}
            <div className="space-y-3">
              <h4 className="font-medium">Source 1</h4>
              <Select value={source1?.type || ''} onValueChange={(value) => {
                setSource1(null);
                if (value) {
                  setSource1({ type: value as any, id: '', name: '' });
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trading_file">Trading Files</SelectItem>
                  <SelectItem value="honest_simulation">Honest Backtest</SelectItem>
                </SelectContent>
              </Select>

              {source1?.type === 'trading_file' && (
                <>
                  <Select value={source1?.id || ''} onValueChange={(value) => {
                    const file = tradingFiles.find(f => f.id === value);
                    if (file) {
                      setSource1({ ...source1, id: value, name: file.filename });
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select trading file" />
                    </SelectTrigger>
                    <SelectContent>
                      {tradingFiles.map(file => (
                        <SelectItem key={file.id} value={file.id}>
                          {file.filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {source1?.id && tradingFiles.find(f => f.id === source1.id)?.analysis_modes.length > 0 && (
                    <Select value={source1?.analysis_mode || ''} onValueChange={(value) => {
                      setSource1({ ...source1, analysis_mode: value });
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select analysis mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {tradingFiles.find(f => f.id === source1.id)?.analysis_modes.map(mode => (
                          <SelectItem key={mode} value={mode}>
                            {mode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              )}

              {source1?.type === 'honest_simulation' && (
                <Select value={source1?.id || ''} onValueChange={(value) => {
                  const sim = honestSimulations.find(s => s.id === value);
                  if (sim) {
                    setSource1({ ...source1, id: value, name: sim.name });
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select simulation" />
                  </SelectTrigger>
                  <SelectContent>
                    {honestSimulations.map(sim => (
                      <SelectItem key={sim.id} value={sim.id}>
                        {sim.name} ({sim.filename})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Source 2 */}
            <div className="space-y-3">
              <h4 className="font-medium">Source 2</h4>
              <Select value={source2?.type || ''} onValueChange={(value) => {
                setSource2(null);
                if (value) {
                  setSource2({ type: value as any, id: '', name: '' });
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trading_file">Trading Files</SelectItem>
                  <SelectItem value="honest_simulation">Honest Backtest</SelectItem>
                </SelectContent>
              </Select>

              {source2?.type === 'trading_file' && (
                <>
                  <Select value={source2?.id || ''} onValueChange={(value) => {
                    const file = tradingFiles.find(f => f.id === value);
                    if (file) {
                      setSource2({ ...source2, id: value, name: file.filename });
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select trading file" />
                    </SelectTrigger>
                    <SelectContent>
                      {tradingFiles.map(file => (
                        <SelectItem key={file.id} value={file.id}>
                          {file.filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {source2?.id && tradingFiles.find(f => f.id === source2.id)?.analysis_modes.length > 0 && (
                    <Select value={source2?.analysis_mode || ''} onValueChange={(value) => {
                      setSource2({ ...source2, analysis_mode: value });
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select analysis mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {tradingFiles.find(f => f.id === source2.id)?.analysis_modes.map(mode => (
                          <SelectItem key={mode} value={mode}>
                            {mode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              )}

              {source2?.type === 'honest_simulation' && (
                <Select value={source2?.id || ''} onValueChange={(value) => {
                  const sim = honestSimulations.find(s => s.id === value);
                  if (sim) {
                    setSource2({ ...source2, id: value, name: sim.name });
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select simulation" />
                  </SelectTrigger>
                  <SelectContent>
                    {honestSimulations.map(sim => (
                      <SelectItem key={sim.id} value={sim.id}>
                        {sim.name} ({sim.filename})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <Button 
              onClick={handleComparePatterns}
              disabled={!source1?.id || !source2?.id || loading}
              size="lg"
            >
              {loading ? 'Comparing...' : 'Compare Patterns'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparison && (
        <>
          {/* Summary */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Comparison Summary</CardTitle>
                <CardDescription>
                  {source1?.name} vs {source2?.name}
                </CardDescription>
              </div>
              <Button onClick={handleExportCSV} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{comparison.identical_patterns}</div>
                  <div className="text-sm text-muted-foreground">
                    Identical ({getPercentage(comparison.identical_patterns, comparison.comparison_details.length)}%)
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{comparison.different_value_patterns}</div>
                  <div className="text-sm text-muted-foreground">
                    Different ({getPercentage(comparison.different_value_patterns, comparison.comparison_details.length)}%)
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{comparison.only_in_source1}</div>
                  <div className="text-sm text-muted-foreground">
                    Only S1 ({getPercentage(comparison.only_in_source1, comparison.comparison_details.length)}%)
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{comparison.only_in_source2}</div>
                  <div className="text-sm text-muted-foreground">
                    Only S2 ({getPercentage(comparison.only_in_source2, comparison.comparison_details.length)}%)
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{comparison.total_patterns_source1}</div>
                  <div className="text-sm text-muted-foreground">Source 1</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{comparison.total_patterns_source2}</div>
                  <div className="text-sm text-muted-foreground">Source 2</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pattern Details */}
          <Card>
            <CardHeader>
              <CardTitle>Pattern Details</CardTitle>
              <CardDescription>Detailed comparison of individual patterns</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Controls */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search patterns..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Patterns</SelectItem>
                    <SelectItem value="identical">Identical</SelectItem>
                    <SelectItem value="different_values">Different Values</SelectItem>
                    <SelectItem value="only_in_source1">Only in Source 1</SelectItem>
                    <SelectItem value="only_in_source2">Only in Source 2</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pattern">Pattern</SelectItem>
                    <SelectItem value="frequency_diff">Frequency Difference</SelectItem>
                    <SelectItem value="success_rate_diff">Success Rate Difference</SelectItem>
                    <SelectItem value="source1_frequency">Source 1 Frequency</SelectItem>
                    <SelectItem value="source2_frequency">Source 2 Frequency</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  {sortOrder === 'asc' ? 'Asc' : 'Desc'}
                </Button>
              </div>

              {/* Pattern List */}
              <div className="space-y-2">
                {paginatedPatterns.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No patterns found matching your criteria
                  </div>
                ) : (
                  paginatedPatterns.map((pattern, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedPattern(pattern)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getStatusBadge(pattern.status)}
                            <span className="text-sm font-mono text-muted-foreground">
                              Pattern: {pattern.pattern || 'N/A'}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Frequency:</span>
                              <div className="flex items-center gap-1">
                                {pattern.source1_data?.frequency || 0} → {pattern.source2_data?.frequency || 0}
                                {getChangeIcon(pattern.differences?.frequency_diff)}
                                <span className={`font-medium ${
                                  pattern.differences?.frequency_diff ? 
                                    (pattern.differences.frequency_diff > 0 ? 'text-green-600' : 
                                     pattern.differences.frequency_diff < 0 ? 'text-red-600' : 'text-gray-600') : 'text-gray-600'
                                }`}>
                                  {formatChange(pattern.differences?.frequency_diff)}
                                </span>
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Success Rate:</span>
                              <div className="flex items-center gap-1">
                                {(pattern.source1_data?.success_rate || 0).toFixed(2)}% → {(pattern.source2_data?.success_rate || 0).toFixed(2)}%
                                {getChangeIcon(pattern.differences?.success_rate_diff)}
                                <span className={`font-medium ${
                                  pattern.differences?.success_rate_diff ? 
                                    (pattern.differences.success_rate_diff > 0 ? 'text-green-600' : 
                                     pattern.differences.success_rate_diff < 0 ? 'text-red-600' : 'text-gray-600') : 'text-gray-600'
                                }`}>
                                  {formatChange(pattern.differences?.success_rate_diff)}%
                                </span>
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Total Sequences:</span>
                              <div className="flex items-center gap-1">
                                {pattern.source1_data?.total_sequences || 0} → {pattern.source2_data?.total_sequences || 0}
                                {getChangeIcon(pattern.differences?.total_sequences_diff)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages} ({filteredPatterns.length} patterns)
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Pattern Detail Modal */}
      <CrossPatternDetailModal
        pattern={selectedPattern}
        isOpen={!!selectedPattern}
        onClose={() => setSelectedPattern(null)}
        source1Name={source1?.name || 'Source 1'}
        source2Name={source2?.name || 'Source 2'}
      />
    </div>
  );
};